import { useCallback, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { AlertTriangle, Building2, Crown, Globe2, Mail, ShieldCheck, Sparkles, UserPlus } from "lucide-react";
import {
  assignAdminRole,
  deactivateAdminUser,
  fetchAdminUsers,
  fetchCompanies,
  fetchRoles,
  fetchWorkspaceInvites,
  getAdminUser,
  getTenantName,
  hasPermission,
  inviteWorkspaceMember,
  reactivateAdminUser,
} from "../api/adminClient";
import AdminConsoleTabs from "./AdminConsoleTabs";

/* Role identity colours, drawn from the dashboard brand tokens (--dash-brand
   #004f73, --dash-brand-light #0a7ea4, --dash-cyan #22d3ee) — brand + cyan
   only, no lavender, per dashboard.css.

   The three `mark` hues are validated (dataviz six checks, --pairs all, since
   a donut can put any two segments side by side) against white and the dark
   surface #0f172a. Light worst pair: CVD ΔE 16.5, normal 26.0. Dark worst
   pair: CVD ΔE 17.0, normal 17.1.

   Only three slots carry a chart hue — gold and peach are fundamentally
   confusable (ΔE 6.0 deutan), as are rose and brand blue (3.4 protan), so
   rather than seat a fourth conflicting hue the donut folds roles 4+ into one
   neutral "Other roles" segment that the legend names. Badges are free to use
   more tints: a badge carries its role name as text, so hue is never the only
   identity channel there. */
const ROLE_SERIES = [
  { mark: "#1668a8", markDark: "#1a6aab", tint: "#e2eef8", ink: "#0c4d78" }, // brand blue
  { mark: "#ef7359", markDark: "#d9613c", tint: "#fdeae4", ink: "#a8412a" }, // peach
  { mark: "#22c3d4", markDark: "#19a5b8", tint: "#dcf3f8", ink: "#0b6070" }, // brand cyan
  { mark: "#94a3b8", markDark: "#7d8ca3", tint: "#fbf0d6", ink: "#8a6206" }, // gold badge
  { mark: "#94a3b8", markDark: "#7d8ca3", tint: "#eef3f8", ink: "#465a69" }, // neutral
];
const OTHER_SERIES = { mark: "#94a3b8", markDark: "#7d8ca3", tint: "#eef3f8", ink: "#465a69" };
/* Legacy admins with no explicit role. Deliberately a neutral, one step darker
   than OTHER_SERIES so the two de-emphasised buckets stay tellable apart while
   neither competes with a real role's hue. */
const UNASSIGNED_SERIES = { mark: "#64748b", markDark: "#8b9bb0", tint: "#eef3f8", ink: "#465a69" };
const CHART_SLOTS = 3; // slots beyond this fold into one "Other roles" segment

/* Deep, brand-tinted elevation — shadows carry the navy of --dash-brand rather
   than neutral black, which is what reads as "polished" instead of "grey". */
const CARD =
  "rounded-[20px] border border-slate-200/80 dark:border-slate-700/70 bg-white dark:bg-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-12px_rgba(0,79,115,0.18)]";
/* Hover lift. `motion-reduce:` neutralises both the translate and the shadow
   swap, so the card just sits still rather than moving a shorter distance. */
const CARD_HOVER =
  "transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:border-[#004f73]/25 hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_22px_44px_-16px_rgba(0,79,115,0.32)] motion-reduce:transition-none motion-reduce:hover:translate-y-0";

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

/* Motion is opt-out, not decoration: everything below collapses to its final
   value when the OS asks for reduced motion, rather than just running faster. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/** Ease-out count-up on a rAF loop. Re-runs whenever `target` changes, so the
 *  figure re-animates after a save instead of snapping. */
function useCountUp(target, { duration = 900, reduced = false } = {}) {
  const [value, setValue] = useState(reduced ? target : 0);
  useEffect(() => {
    if (reduced || !target) {
      setValue(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  return value;
}

/** Flips true one frame after mount so CSS transitions have a "from" state to
 *  animate out of. */
function useEntrance(reduced, ready = true) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ready) {
      setShown(false);
      return undefined;
    }
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const t = setTimeout(() => setShown(true), 40);
    return () => clearTimeout(t);
  }, [reduced, ready]);
  return shown;
}

function initialsOf(user) {
  const first = (user.first_name || "").trim();
  const last = (user.last_name || "").trim();
  if (first || last) return `${first[0] || ""}${last[0] || ""}`.toUpperCase();
  return (user.email || "?").slice(0, 2).toUpperCase();
}

function displayName(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function Avatar({ user, series, size = 40 }) {
  const [broken, setBroken] = useState(false);
  const px = { width: size, height: size };
  if (user.profilepic && !broken) {
    return (
      <img
        src={user.profilepic}
        alt=""
        style={px}
        onError={() => setBroken(true)}
        className="shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-slate-800 shadow-[0_2px_6px_-1px_rgba(0,79,115,0.28)]"
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{ ...px, backgroundColor: series.tint, color: series.ink }}
      className="grid shrink-0 place-items-center rounded-full text-xs font-bold ring-2 ring-white dark:ring-slate-800 shadow-[0_2px_6px_-1px_rgba(0,79,115,0.28)]"
    >
      {initialsOf(user)}
    </span>
  );
}

function RoleBadge({ label, series, isProtected, muted }) {
  const Icon = isProtected ? Crown : Sparkles;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_1px_2px_rgba(15,23,42,0.06)]"
      style={{ backgroundColor: series.tint, color: series.ink }}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {label}
      {muted ? <span className="font-medium opacity-70">· unassigned</span> : null}
    </span>
  );
}

/* Part-to-whole donut. The hole carries the total as a hero figure so the
   "two close slices" case still resolves to exact numbers, and the legend
   repeats every count and share — identity and value never rest on hue alone.
   Segments are separated by a 2px surface-coloured gap, not strokes. */
function RoleDonut({ distribution, total, size = 168 }) {
  const reduced = usePrefersReducedMotion();
  const shown = useEntrance(reduced);
  const centre = useCountUp(total, { reduced });
  /* Hover/focus is shared between the ring and the legend, so pointing at
     either one highlights both. The hole doubles as the detail-on-demand
     surface — cheaper to read than a floating tooltip, and it can't be
     clipped by the card. */
  const [active, setActive] = useState(null);

  const stroke = 22;
  const strokeActive = 27;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const gap = 2;
  const activeItem = distribution.find((d) => d.key === active) || null;

  /* Segments sweep in one after another, each starting as the previous one
     lands, so the ring draws itself clockwise rather than all arcs growing at
     once. Delay is cumulative on the share each segment occupies. */
  let offset = 0;
  let delay = 0;
  const arcs = distribution.map((d) => {
    const share = d.count / total;
    const raw = share * circumference;
    const arc = {
      key: d.key,
      label: d.label,
      count: d.count,
      color: d.series.mark,
      len: Math.max(raw - gap, 0.5),
      rest: circumference,
      offset,
      delay,
      duration: Math.max(share * 900, 180),
    };
    offset += raw;
    delay += arc.duration;
    return arc;
  });

  return (
    <div className="flex flex-wrap items-center gap-5">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="-rotate-90"
          role="img"
          aria-label={`Role distribution across ${total} admins`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-slate-100 dark:stroke-slate-800"
          />
          {arcs.map((a) => {
            const isActive = active === a.key;
            const dimmed = active && !isActive;
            return (
              <circle
                key={a.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={a.color}
                strokeWidth={isActive ? strokeActive : stroke}
                strokeDasharray={`${shown ? a.len : 0} ${a.rest - (shown ? a.len : 0)}`}
                strokeDashoffset={-a.offset}
                strokeLinecap="butt"
                opacity={dimmed ? 0.32 : 1}
                onMouseEnter={() => setActive(a.key)}
                onMouseLeave={() => setActive(null)}
                style={{
                  // Only the stroke is hoverable — the ring's hollow centre and
                  // the space outside it must stay click-through.
                  pointerEvents: "stroke",
                  cursor: "pointer",
                  transition: reduced
                    ? undefined
                    : `stroke-dasharray ${a.duration}ms cubic-bezier(0.33,1,0.68,1) ${a.delay}ms, stroke-width 180ms ease-out, opacity 180ms ease-out`,
                }}
              >
                <title>{`${a.label}: ${a.count} of ${total}`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-items-center px-6 text-center">
          {activeItem ? (
            <>
              <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 dark:text-slate-100">
                {activeItem.count}
              </span>
              <span
                className="mt-1 line-clamp-2 text-[10px] font-semibold uppercase leading-tight tracking-[0.1em]"
                style={{ color: activeItem.series.ink }}
              >
                {activeItem.label}
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 dark:text-slate-100">
                {centre}
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                admins
              </span>
            </>
          )}
        </div>
      </div>
      <ul className="min-w-[9rem] flex-1 space-y-0.5">
        {distribution.map((d, i) => {
          const isActive = active === d.key;
          return (
            <li
              key={d.key}
              style={{
                opacity: shown ? 1 : 0,
                transform: shown ? "none" : "translateX(-6px)",
                transition: reduced
                  ? undefined
                  : `opacity 380ms ease-out ${260 + i * 90}ms, transform 380ms cubic-bezier(0.33,1,0.68,1) ${
                      260 + i * 90
                    }ms`,
              }}
            >
              {/* A button, not a bare row: the ring's hover detail has to be
                  reachable by keyboard too, and focus drives the same state. */}
              <button
                type="button"
                onMouseEnter={() => setActive(d.key)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(d.key)}
                onBlur={() => setActive(null)}
                aria-label={`${d.label}: ${d.count} of ${total} admins`}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004f73]/30 ${
                  isActive ? "bg-slate-100 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                }`}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-white transition-transform duration-200 dark:ring-slate-900"
                  style={{
                    backgroundColor: d.series.mark,
                    transform: isActive ? "scale(1.45)" : "none",
                  }}
                  aria-hidden
                />
                <span
                  className={`min-w-0 flex-1 truncate transition-colors ${
                    isActive
                      ? "font-semibold text-slate-900 dark:text-slate-100"
                      : "font-medium text-slate-600 dark:text-slate-300"
                  }`}
                  title={d.label}
                >
                  {d.label}
                </span>
                <span className="shrink-0 font-bold tabular-nums text-slate-900 dark:text-slate-100">
                  {d.count}
                </span>
                <span className="w-9 shrink-0 text-right tabular-nums text-slate-400">
                  {Math.round((d.count / total) * 100)}%
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [assignForm, setAssignForm] = useState({ roleId: "", companyIds: [] });
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [disabledUsers, setDisabledUsers] = useState([]);
  const [listFilter, setListFilter] = useState("active");
  const [reactivatingId, setReactivatingId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role_id: "",
  });
  // Self-deactivation is refused server-side too; knowing the caller here just
  // lets the row disable the control instead of failing after the click.
  const currentAdminId = useMemo(() => {
    const u = getAdminUser();
    return u ? String(u._id || u.id || "") : "";
  }, []);
  const workspaceTenantId = useMemo(() => {
    const u = getAdminUser();
    return u?.tenant_id ? String(u.tenant_id) : "";
  }, []);
  const canManageUsers = hasPermission("admin:manage_users");
  const canInviteWorkspace = Boolean(workspaceTenantId && canManageUsers);
  const tenantLabel = getTenantName() || "your organization";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const admin = getAdminUser();
      const tenantId = admin?.tenant_id ? String(admin.tenant_id) : "";
      const canInvite = tenantId && canManageUsers;
      const requests = [
        fetchAdminUsers(),
        fetchAdminUsers({ status: "disabled" }),
        fetchRoles(),
        fetchCompanies(),
      ];
      if (canInvite) {
        requests.push(fetchWorkspaceInvites("pending"));
      }
      const results = await Promise.all(requests);
      setUsers(results[0].data?.users || []);
      setDisabledUsers(results[1].data?.users || []);
      setRoles(results[2].data?.roles || []);
      setCompanies(results[3].data?.companies || []);
      setPendingInvites(canInvite ? results[4]?.data?.invites || [] : []);
    } catch (err) {
      toast.error(err.message || "Could not load admins.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const roleById = (id) => roles.find((r) => r.id === id);

  /* Colour follows the role entity, not its count: the protected Super Admin
     role always takes slot 0 (brand blue), everything else takes a slot by
     stable id order, so adding an admin never repaints an existing role.

     Only roles that actually have admins are seated. Slotting every role in
     role_master meant an unused role could eat a palette slot and push a role
     that IS in use out to the neutral "other" colour. */
  const slotIndexByRole = useMemo(() => {
    const inUse = new Set(users.map((u) => u.role_id).filter(Boolean));
    const ordered = roles
      .filter((r) => inUse.has(r.id))
      .sort((a, b) => {
        if (a.is_protected !== b.is_protected) return a.is_protected ? -1 : 1;
        return String(a.id).localeCompare(String(b.id));
      });
    const map = new Map();
    ordered.forEach((role, i) => map.set(role.id, i));
    return map;
  }, [roles, users]);

  /* Legacy admins with no role_id fall back to full Super Admin permissions
     server-side, and list_admin_users reports them with role_name
     "Super Admin". They are NOT the same entity as the real role, though —
     grouping them together would hide that they still need an explicit
     assignment, so they get their own neutral slot and their own label. */
  const seriesFor = useCallback(
    (user) => {
      if (!user.role_id) return OTHER_SERIES;
      const i = slotIndexByRole.get(user.role_id);
      return i === undefined ? OTHER_SERIES : ROLE_SERIES[i] || OTHER_SERIES;
    },
    [slotIndexByRole]
  );

  const summary = useMemo(() => {
    const total = users.length;
    const globalScoped = users.filter((u) => !u.company_ids?.length).length;

    const counts = new Map();
    for (const u of users) {
      const key = u.role_id || "__unassigned__";
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else
        counts.set(key, {
          key,
          // Never reuse the fallback's reported "Super Admin" name here — it
          // would render a second, identically-named legend row.
          label: u.role_id ? u.role_name || "Unnamed role" : "Unassigned (legacy)",
          count: 1,
          slot: u.role_id ? slotIndexByRole.get(u.role_id) ?? Infinity : Infinity,
          series: seriesFor(u),
        });
    }

    /* Only CHART_SLOTS roles get their own validated hue; extra roles collapse
       into one neutral segment rather than reusing a confusable colour. The
       unassigned group is never folded in — "these admins still need a role"
       is the actionable signal on this card, so it keeps its own row. */
    const all = [...counts.values()].sort((a, b) => b.count - a.count);
    const unassigned = all.find((d) => d.key === "__unassigned__");
    const named = all.filter((d) => d.key !== "__unassigned__");
    const distribution = named.filter((d) => d.slot < CHART_SLOTS);
    const tail = named.filter((d) => d.slot >= CHART_SLOTS);
    if (tail.length) {
      distribution.push({
        key: "__other__",
        label: tail.length === 1 ? tail[0].label : `${tail.length} other roles`,
        count: tail.reduce((sum, d) => sum + d.count, 0),
        series: OTHER_SERIES,
      });
    }
    if (unassigned) distribution.push({ ...unassigned, series: UNASSIGNED_SERIES });

    return {
      total,
      globalScoped,
      companyScoped: total - globalScoped,
      distribution,
    };
  }, [users, seriesFor, slotIndexByRole]);

  const recentlyAdded = useMemo(
    () =>
      [...users]
        .filter((u) => u.created_date)
        .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
        .slice(0, 4),
    [users]
  );

  const openAssign = (user) => {
    setEditingId(user.id);
    setCompanySearch("");
    setAssignForm({
      roleId: user.role_id || roles.find((r) => r.is_protected)?.id || "",
      companyIds: user.company_ids || [],
    });
  };

  const toggleCompany = (id) => {
    setAssignForm((f) => ({
      ...f,
      companyIds: f.companyIds.includes(id)
        ? f.companyIds.filter((c) => c !== id)
        : [...f.companyIds, id],
    }));
  };

  const selectedRoleIsGlobal = !!roleById(assignForm.roleId)?.is_protected;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!assignForm.roleId) {
      toast.error("Choose a role.");
      return;
    }
    setSaving(true);
    try {
      await assignAdminRole(editingId, {
        roleId: assignForm.roleId,
        companyIds: selectedRoleIsGlobal ? [] : assignForm.companyIds,
      });
      toast.success("Role assigned.");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.message || "Could not assign role.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmTarget) return;
    setDeactivating(true);
    try {
      await deactivateAdminUser(confirmTarget.id);
      toast.success(`${displayName(confirmTarget)} deactivated.`);
      setConfirmTarget(null);
      load();
    } catch (err) {
      toast.error(err.message || "Could not deactivate this admin.");
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async (user) => {
    setReactivatingId(user.id);
    try {
      await reactivateAdminUser(user.id);
      toast.success(`${displayName(user)} reactivated.`);
      load();
    } catch (err) {
      toast.error(err.message || "Could not reactivate this admin.");
    } finally {
      setReactivatingId(null);
    }
  };

  const invitableRoles = useMemo(
    () => roles.filter((r) => !r.is_protected),
    [roles]
  );

  const openInviteModal = () => {
    const defaultRole =
      invitableRoles.find((r) => r.name === "Company Admin") || invitableRoles[0];
    setInviteForm({
      email: "",
      first_name: "",
      last_name: "",
      role_id: defaultRole?.id || "",
    });
    setInviteOpen(true);
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await inviteWorkspaceMember({
        email: inviteForm.email.trim(),
        first_name: inviteForm.first_name.trim(),
        last_name: inviteForm.last_name.trim(),
        role_id: inviteForm.role_id || undefined,
      });
      if (res.data?.email_sent === false) {
        toast.error(
          res.data?.email_mode === "error"
            ? "Invite saved, but the email failed to send. Check mail settings and resend."
            : "Invite saved, but email was not delivered. Check mail settings and resend."
        );
      } else {
        toast.success(res.message || "Invitation sent.");
      }
      setInviteOpen(false);
      load();
    } catch (err) {
      toast.error(err.message || "Could not send invitation.");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (invite) => {
    setInviting(true);
    try {
      const res = await inviteWorkspaceMember({
        email: invite.email,
        first_name: invite.first_name || "",
        last_name: invite.last_name || "",
        role_id: invite.role_id || undefined,
      });
      if (res.data?.email_sent === false) {
        toast.error(
          "Invite updated, but the email failed to send. Check mail settings."
        );
      } else {
        toast.success(res.message || "Invitation resent.");
      }
      load();
    } catch (err) {
      toast.error(err.message || "Could not resend invitation.");
    } finally {
      setInviting(false);
    }
  };

  const gaugePct = summary.total ? (summary.globalScoped / summary.total) * 100 : 0;
  const reducedMotion = usePrefersReducedMotion();
  const shown = useEntrance(reducedMotion, !loading);
  const totalCount = useCountUp(summary.total, { reduced: reducedMotion });
  const globalCount = useCountUp(summary.globalScoped, { reduced: reducedMotion, duration: 700 });
  const scopedCount = useCountUp(summary.companyScoped, { reduced: reducedMotion, duration: 700 });

  /** Staggered lift-and-fade for a summary card or feed row. */
  const enter = (i) => ({
    opacity: shown ? 1 : 0,
    transform: shown ? "none" : "translateY(10px)",
    transition: reducedMotion
      ? undefined
      : `opacity 460ms ease-out ${i * 90}ms, transform 460ms cubic-bezier(0.33,1,0.68,1) ${i * 90}ms`,
  });

  const baseUsers = listFilter === "disabled" ? disabledUsers : users;
  const listedUsers = userSearch.trim()
    ? baseUsers.filter((u) => {
        const q = userSearch.toLowerCase();
        const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
        return name.includes(q) || (u.email || "").toLowerCase().includes(q);
      })
    : baseUsers;

  const filteredCompanies = companySearch.trim()
    ? companies.filter((c) =>
        (c.company_name || "").toLowerCase().includes(companySearch.toLowerCase())
      )
    : companies;

  return (
    <div className="min-h-full w-full bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-center" />
      <div className="w-full px-7 py-8">
        <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0a7ea4]">
              Admin
            </p>
            <h1 className="text-[28px] font-bold leading-tight text-slate-900 dark:text-slate-100">
              Admin Users
            </h1>
            <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
              Assign each admin a role and, for company-scoped roles, which companies they can access.
              {canInviteWorkspace ? (
                <>
                  {" "}
                  Invite teammates to <strong>{tenantLabel}</strong> by email — they&apos;ll join this
                  workspace only.
                </>
              ) : null}
            </p>
          </div>
          {canInviteWorkspace ? (
            <button
              type="button"
              onClick={openInviteModal}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-b from-[#004f73] to-[#0a7ea4] px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_3px_10px_-2px_rgba(0,79,115,0.45)] transition-all hover:from-[#005a82] hover:to-[#0b8eb8] active:translate-y-px"
            >
              <Mail className="h-4 w-4" aria-hidden />
              Invite member
            </button>
          ) : null}
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#004f73]">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="mb-7 grid grid-cols-1 gap-5 lg:grid-cols-12">
              {/* Hero counter + brand-gradient gauge */}
              <div className={`${CARD} ${CARD_HOVER} p-6 lg:col-span-3`} style={enter(0)}>
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Total admin users
                </p>
                <p className="mt-2 text-[56px] font-bold leading-none tracking-tight text-slate-900 dark:text-slate-100">
                  {totalCount}
                </p>
                <div className="mt-5 h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800 shadow-[inset_0_1px_3px_rgba(15,23,42,0.12)]">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#004f73] via-[#0a7ea4] to-[#22c3d4] shadow-[0_1px_2px_rgba(0,79,115,0.35)]"
                    style={{
                      width: `${shown ? gaugePct : 0}%`,
                      transition: reducedMotion
                        ? undefined
                        : "width 900ms cubic-bezier(0.33,1,0.68,1) 120ms",
                    }}
                  />
                </div>
                <div className="mt-4 space-y-2.5">
                  <p className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-300">
                    <Globe2 className="h-4 w-4 text-[#004f73] dark:text-[#22c3d4]" aria-hidden />
                    <span className="flex-1 font-medium">Unrestricted</span>
                    <span className="font-bold tabular-nums">{globalCount}</span>
                  </p>
                  <p className="flex items-center gap-2.5 text-xs text-slate-600 dark:text-slate-300">
                    <Building2 className="h-4 w-4 text-[#22c3d4]" aria-hidden />
                    <span className="flex-1 font-medium">Company-scoped</span>
                    <span className="font-bold tabular-nums">{scopedCount}</span>
                  </p>
                </div>
              </div>

              {/* Role distribution donut */}
              <div className={`${CARD} ${CARD_HOVER} p-6 lg:col-span-5`} style={enter(1)}>
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Role distribution
                </p>
                {summary.total ? (
                  <RoleDonut distribution={summary.distribution} total={summary.total} />
                ) : (
                  <p className="text-sm text-slate-400">No admins yet.</p>
                )}
              </div>

              {/* Recently added */}
              <div className={`${CARD} ${CARD_HOVER} p-6 lg:col-span-4`} style={enter(2)}>
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Recently added
                </p>
                {recentlyAdded.length ? (
                  <ul className="space-y-3.5">
                    {recentlyAdded.map((u, i) => (
                      <li
                        key={u.id}
                        className="group/feed -mx-2 flex items-center gap-3 rounded-xl px-2 py-1 transition-colors duration-200 hover:bg-slate-50 dark:hover:bg-slate-800/60"
                        style={{
                          opacity: shown ? 1 : 0,
                          transform: shown ? "none" : "translateX(8px)",
                          transition: reducedMotion
                            ? undefined
                            : `opacity 400ms ease-out ${320 + i * 80}ms, transform 400ms cubic-bezier(0.33,1,0.68,1) ${
                                320 + i * 80
                              }ms`,
                        }}
                      >
                        <span className="transition-transform duration-300 ease-out group-hover/feed:scale-[1.08] motion-reduce:transition-none motion-reduce:group-hover/feed:scale-100">
                          <Avatar user={u} series={seriesFor(u)} size={36} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {displayName(u)}
                          </span>
                          <span className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                            <UserPlus className="h-3 w-3" aria-hidden />
                            Account created
                          </span>
                        </span>
                        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-slate-500 transition-colors duration-200 group-hover/feed:bg-[#e2eef8] group-hover/feed:text-[#0c4d78] dark:bg-slate-800 dark:text-slate-400 dark:group-hover/feed:bg-slate-700">
                          {relativeTime(u.created_date)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400">No creation dates recorded.</p>
                )}
              </div>
            </div>

            <AdminConsoleTabs active="users" />

            {canInviteWorkspace && pendingInvites.length ? (
              <div className={`${CARD} mb-5 overflow-hidden`}>
                <div className="border-b border-slate-100 px-7 py-4 dark:border-slate-800">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                    Pending invitations
                  </p>
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {pendingInvites.map((inv) => (
                    <li
                      key={inv.id}
                      className="flex flex-wrap items-center gap-3 px-7 py-4 text-sm"
                    >
                      <Mail className="h-4 w-4 shrink-0 text-[#0a7ea4]" aria-hidden />
                      <span className="min-w-0 flex-1 font-medium text-slate-900 dark:text-slate-100">
                        {inv.email}
                      </span>
                      {inv.role_name ? (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {inv.role_name}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleResendInvite(inv)}
                        disabled={inviting}
                        className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#004f73] transition-colors hover:bg-[#e2eef8] disabled:opacity-50 dark:text-[#22c3d4] dark:hover:bg-slate-800"
                      >
                        Resend
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className={`${CARD} overflow-hidden`}>
              <div className="flex flex-wrap items-center gap-2 px-7 pt-5 pb-1">
                {[
                  { id: "active", label: "Active", count: users.length },
                  { id: "disabled", label: "Deactivated", count: disabledUsers.length },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => { setListFilter(opt.id); setUserSearch(""); }}
                    className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
                      listFilter === opt.id
                        ? "bg-[#004f73] text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700"
                    }`}
                  >
                    {opt.label}
                    <span className="ml-1.5 tabular-nums opacity-80">{opt.count}</span>
                  </button>
                ))}
                <div className="ml-auto">
                  <input
                    type="search"
                    placeholder="Search by name or email…"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="w-56 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3.5 py-1.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-7 py-4 text-left font-bold">Admin</th>
                      <th className="px-7 py-4 text-left font-bold">Role</th>
                      <th className="px-7 py-4 text-left font-bold">Companies</th>
                      <th className="px-7 py-4 text-right font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {listedUsers.map((user) => {
                      const series = seriesFor(user);
                      const scoped = !!user.company_ids?.length;
                      const protectedRole =
                        !!roleById(user.role_id)?.is_protected || user.is_fallback;
                      const isSelf = !!currentAdminId && String(user.id) === currentAdminId;
                      return (
                        <tr
                          key={user.id}
                          className="group/row transition-colors hover:bg-[#004f73]/[0.035] dark:hover:bg-slate-800/40"
                        >
                          <td className="relative px-7 py-5">
                            {/* Accent rail slides in from the left edge on hover */}
                            <span
                              aria-hidden
                              className="absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 rounded-r bg-gradient-to-b from-[#0a7ea4] to-[#22c3d4] transition-transform duration-300 ease-out group-hover/row:scale-y-100 motion-reduce:transition-none"
                            />
                            <div className="flex items-center gap-3.5">
                              <span className="transition-transform duration-300 ease-out group-hover/row:scale-[1.08] motion-reduce:transition-none motion-reduce:group-hover/row:scale-100">
                                <Avatar user={user} series={series} />
                              </span>
                              <div className="min-w-0">
                                <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                                  {displayName(user)}
                                </div>
                                <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-7 py-5">
                            <span className="inline-block transition-transform duration-300 ease-out group-hover/row:-translate-y-0.5 motion-reduce:transition-none motion-reduce:group-hover/row:translate-y-0">
                              <RoleBadge
                                label={user.role_name}
                                series={series}
                                isProtected={protectedRole}
                                muted={user.is_fallback}
                              />
                            </span>
                          </td>
                          <td className="px-7 py-5">
                            {scoped ? (
                              <span
                                className="font-medium text-slate-600 dark:text-slate-300"
                                title={user.company_names?.join(", ")}
                              >
                                {user.company_names?.length
                                  ? user.company_names.join(", ")
                                  : `${user.company_ids.length} companies`}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
                                <ShieldCheck className="h-4 w-4" aria-hidden />
                                All companies
                              </span>
                            )}
                          </td>
                          <td className="px-7 py-5">
                            <div className="flex flex-col items-end gap-1.5">
                              {listFilter === "disabled" ? (
                                canManageUsers ? (
                                <button
                                  type="button"
                                  onClick={() => handleReactivate(user)}
                                  disabled={reactivatingId === user.id}
                                  className="w-full max-w-[9.5rem] rounded-full border border-[#004f73]/25 bg-gradient-to-b from-white to-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#004f73] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_3px_rgba(0,79,115,0.14)] transition-all hover:border-[#004f73]/40 hover:from-white hover:to-[#e2eef8] disabled:opacity-50 dark:border-slate-600 dark:from-slate-800 dark:to-slate-900 dark:text-[#7fd4e3]"
                                >
                                  {reactivatingId === user.id ? "Restoring…" : "Reactivate"}
                                </button>
                                ) : (
                                  <span className="text-xs text-slate-400">View only</span>
                                )
                              ) : canManageUsers ? (
                                <>
                              <button
                                type="button"
                                onClick={() => openAssign(user)}
                                className="w-full max-w-[9.5rem] rounded-full border border-[#004f73]/25 bg-gradient-to-b from-white to-slate-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#004f73] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_3px_rgba(0,79,115,0.14)] transition-all hover:border-[#004f73]/40 hover:from-white hover:to-[#e2eef8] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_2px_7px_rgba(0,79,115,0.22)] active:translate-y-px active:shadow-[inset_0_2px_4px_rgba(0,79,115,0.18)] dark:border-slate-600 dark:from-slate-800 dark:to-slate-900 dark:text-[#7fd4e3]"
                              >
                                Assign role
                              </button>
                              <button
                                type="button"
                                disabled={isSelf}
                                onClick={() => setConfirmTarget(user)}
                                title={
                                  isSelf
                                    ? "You can't deactivate your own account"
                                    : "Revoke this admin's access"
                                }
                                className="w-full max-w-[9.5rem] rounded-full border border-slate-200 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400 transition-all hover:border-red-300 hover:bg-red-50 hover:text-red-600 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-slate-200 disabled:hover:bg-transparent disabled:hover:text-slate-400 dark:border-slate-700 dark:hover:border-red-900 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                              >
                                Deactivate
                              </button>
                                </>
                              ) : (
                                <span className="text-xs text-slate-400">View only</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!listedUsers.length ? (
                <p className="px-7 py-14 text-center text-sm text-slate-400">
                  {userSearch.trim()
                    ? `No admins match "${userSearch}".`
                    : listFilter === "disabled"
                      ? "No deactivated admins."
                      : "No admin users yet."}
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>

      {confirmTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="deactivate-title"
            className="w-full max-w-md rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)] dark:border-slate-700 dark:bg-slate-900"
          >
            <div className="mb-4 flex items-start gap-3.5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <h2
                  id="deactivate-title"
                  className="text-lg font-bold text-slate-900 dark:text-slate-100"
                >
                  Deactivate this admin?
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {displayName(confirmTarget)}
                  </span>{" "}
                  will immediately lose access and won&apos;t be able to sign in.
                </p>
              </div>
            </div>
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              Their account is disabled, not deleted — past activity and report history stay
              intact. Restore them anytime from the Deactivated list here, or from Create / Reset
              → Reactivate.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2.5">
              <button
                type="button"
                className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setConfirmTarget(null)}
                disabled={deactivating}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={deactivating}
                className="rounded-full bg-gradient-to-b from-red-500 to-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_3px_10px_-2px_rgba(190,18,60,0.5)] transition-all hover:from-red-500 hover:to-red-700 active:translate-y-px disabled:opacity-50"
              >
                {deactivating ? "Deactivating…" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {inviteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-member-title"
            className="w-full max-w-lg rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)] dark:border-slate-700 dark:bg-slate-900"
          >
            <h2
              id="invite-member-title"
              className="mb-1 text-lg font-bold text-slate-900 dark:text-slate-100"
            >
              Invite a workspace member
            </h2>
            <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
              We&apos;ll email them a secure link to join <strong>{tenantLabel}</strong>. Invites
              expire after 7 days.
            </p>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="colleague@company.com"
                  autoComplete="off"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                    First name
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    value={inviteForm.first_name}
                    onChange={(e) => setInviteForm((f) => ({ ...f, first_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                    Last name
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    value={inviteForm.last_name}
                    onChange={(e) => setInviteForm((f) => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Role
                </label>
                <select
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-900 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  value={inviteForm.role_id}
                  onChange={(e) => setInviteForm((f) => ({ ...f, role_id: e.target.value }))}
                  required
                >
                  <option value="" disabled>
                    Choose a role
                  </option>
                  {invitableRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="rounded-full bg-gradient-to-b from-[#004f73] to-[#0a7ea4] px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_3px_10px_-2px_rgba(0,79,115,0.45)] transition-all hover:from-[#005a82] hover:to-[#0b8eb8] active:translate-y-px disabled:opacity-50"
                >
                  {inviting ? "Sending…" : "Send invitation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editingId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-role-title"
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-[20px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)]"
          >
            <h2
              id="assign-role-title"
              className="mb-5 text-lg font-bold text-slate-900 dark:text-slate-100"
            >
              Assign role
            </h2>
            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Role
                </label>
                <select
                  className="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3.5 py-3 text-sm text-slate-900 dark:text-slate-100 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)] focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                  value={assignForm.roleId}
                  onChange={(e) => setAssignForm((f) => ({ ...f, roleId: e.target.value }))}
                  required
                >
                  <option value="" disabled>
                    Choose a role
                  </option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {!selectedRoleIsGlobal ? (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-600 dark:text-slate-300">
                    Companies (scoped access)
                  </label>
                  <input
                    type="search"
                    placeholder="Filter companies…"
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="mb-1.5 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                  />
                  <div className="max-h-48 space-y-0.5 overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800 p-2 shadow-[inset_0_1px_2px_rgba(15,23,42,0.05)]">
                    {filteredCompanies.length ? filteredCompanies.map((c) => (
                      <label
                        key={c.id}
                        className="flex cursor-pointer items-center gap-2.5 rounded-xl px-2 py-1.5 text-sm text-slate-700 dark:text-slate-200 transition-colors hover:bg-white dark:hover:bg-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={assignForm.companyIds.includes(c.id)}
                          onChange={() => toggleCompany(c.id)}
                          className="rounded border-slate-300 text-[#004f73] focus:ring-[#004f73]/30"
                        />
                        {c.company_name}
                      </label>
                    )) : (
                      <p className="px-2 py-3 text-sm text-slate-400">No companies match.</p>
                    )}
                  </div>
                  {!assignForm.companyIds.length ? (
                    <p className="mt-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                      No companies selected — this admin won&apos;t see any company data yet.
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3.5 py-3 text-xs font-medium text-slate-500 dark:text-slate-400">
                  This role has unrestricted (global) access — no company selection needed.
                </p>
              )}

              <div className="flex items-center justify-end gap-2.5 pt-1">
                <button
                  type="button"
                  className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setEditingId(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-full bg-gradient-to-b from-[#0a7ea4] to-[#004f73] px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_3px_10px_-2px_rgba(0,79,115,0.55)] transition-all hover:from-[#0d8cb5] hover:to-[#065a82] active:translate-y-px active:shadow-[inset_0_2px_5px_rgba(0,30,45,0.35)] disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save assignment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
